import {
  getGetDispositiviQueryKey,
  getGetNodoPortaleQueryKey,
  NodoDto,
  ProprietaMisurataDto,
  useEliminaNodo,
} from "@/api";
import { useAuth } from "@/context/useAuth";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Typography from "@/elements/Typography";
import { portaleConfig } from "@/portaliConfig";
import {
  DispositivoStatus,
  TipoDispositivoByTipoNodo,
} from "@/utils/dispositivoUtils";
import {
  classeDiContaminazione,
  splitAndSortProprietaMisurate,
} from "@/utils/proprietaMisurataUtils";
import { useConfirmationDialog } from "@/utils/useConfirmationDialog";
import { Grid, Icon, Tooltip } from "@mui/material";
import Card from "@mui/material/Card";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { DispositivoColor } from "../DispositivoDetails";
import PosizioneNodo from "../PosizioneNodo";
import ProprietaMisurataLabel from "../ProprietaMisurataLabel";

function DispositivoCard({
  dispositivo,
  proprietaMisurate,
  showPosizione,
}: {
  dispositivo: NodoDto;
  proprietaMisurate: ProprietaMisurataDto[];
  showPosizione: boolean;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { askConfirmation, confirmationDialog } = useConfirmationDialog();

  const { mutateAsync: deleteAsync } = useEliminaNodo({
    mutation: {
      onSuccess: () => {
        toast.success(t("dispositivoCard.toast.eliminato"));
      },
      onError: () => {
        toast.error(t("dispositivoCard.toast.erroreEliminazione"));
      },
    },
  });

  const queryClient = useQueryClient();

  const deleteDispositivo = useCallback(
    async (dispositivo: NodoDto) => {
      try {
        await deleteAsync({ id: dispositivo.id });

        await queryClient.invalidateQueries(
          getGetDispositiviQueryKey(
            portaleConfig.id,
            TipoDispositivoByTipoNodo[dispositivo.tipo]
          )
        );
        await queryClient.invalidateQueries(
          getGetNodoPortaleQueryKey(portaleConfig.id, dispositivo.id)
        );

        for (const trasferimento of dispositivo.trasferimentiBreadcrumbs ??
          []) {
          const padre = trasferimento[trasferimento.length - 1].arco.sorgenteId;
          if (padre) {
            await queryClient.invalidateQueries(
              getGetNodoPortaleQueryKey(portaleConfig.id, padre)
            );
          }
        }
      } catch (err) {
        console.error(err);
      }
    },
    [deleteAsync, queryClient]
  );

  const {
    proprietaStandardMisurate,
    proprietaISOMisurate,
    proprietaRmsMisurate,
    proprietaFftMisurate: proprietaFftMisurate,
  } = useMemo(() => {
    return splitAndSortProprietaMisurate(proprietaMisurate);
  }, [proprietaMisurate]);

  const classeContaminazione = useMemo(() => {
    return classeDiContaminazione(proprietaISOMisurate);
  }, [proprietaISOMisurate]);

  const statusIcon = useMemo(() => {
    const status: DispositivoStatus =
      (dispositivo.dispositivoLastStatus as DispositivoStatus) ?? "off";
    switch (status) {
      case "on":
        return (
          <Tooltip title={t("dispositivoCard.status.on")}>
            <Icon color="success">cloud_queue</Icon>
          </Tooltip>
        );

      case "on-manuale":
        return (
          <Tooltip title={t("dispositivoCard.status.onManuale")}>
            <Icon color="success">cloud_queue</Icon>
          </Tooltip>
        );

      case "stop":
        return (
          <Tooltip title={t("dispositivoCard.status.connessoOff")}>
            <Icon color="error">power_settings_new</Icon>
          </Tooltip>
        );

      case "alarm":
        return (
          <Tooltip title={t("dispositivoCard.status.allarme")}>
            <Icon color="warning">warning</Icon>
          </Tooltip>
        );

      case "off":
        return (
          <Tooltip title={t("dispositivoCard.status.nonConnesso")}>
            <Icon color="error">cloud_off</Icon>
          </Tooltip>
        );
    }
  }, [dispositivo, t]);

  const trasferimento =
    dispositivo.breadcrumbs &&
    dispositivo.breadcrumbs[dispositivo.breadcrumbs.length - 1];

  const link = `/nodi/${dispositivo.id}`;

  return (
    <Card sx={{ width: 400, height: 400 }}>
      {confirmationDialog}
      <Box p={2} flex={1}>
        <Box display="flex" flexDirection="column" height="100%">
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography variant="body2" color="text" fontWeight="medium">
                {dispositivo.dispositivoModello!.modello}
              </Typography>

              <Typography variant="h5" fontWeight="bold">
                {dispositivo.dispositivoMatricola}
              </Typography>

              {dispositivo.nome && (
                <Typography variant="h6" mt={-1}>
                  {dispositivo.nome}
                </Typography>
              )}
            </Box>

            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              {statusIcon}
              {dispositivo.dispositivoLastStatus !== "off" && (
                <DispositivoColor nodo={dispositivo} />
              )}
            </Box>
          </Box>
          {showPosizione && (
            <Box sx={{ minHeight: 18 }}>
              <PosizioneNodo nodo={dispositivo} showDate />
            </Box>
          )}

          {trasferimento?.arco?.titolare && (
            <Typography variant="caption" fontWeight="medium">
              {t("dispositivoCard.proprietario")}: <b>{trasferimento.arco.titolare.nome}</b>
            </Typography>
          )}

          {trasferimento?.arco?.trasferimentoModalita && (
            <Typography variant="caption" fontWeight="medium">
              {t("dispositivoCard.modalita")}: <b>{trasferimento.arco.trasferimentoModalita}</b>
            </Typography>
          )}

          {classeContaminazione && (
            <Typography variant="caption" fontWeight="medium" mt={1}>
              {t("dispositivoCard.classeContaminazione")}: <b>{classeContaminazione}</b>
            </Typography>
          )}
          <Box
            mt={2}
            mb={0}
            alignContent="left"
            sx={{ maxHeight: "180px", overflowY: "auto" }}
          >
            <Grid
              container
              rowSpacing={1} // Adjust spacing between grid items
              columnSpacing={1} // Adjust spacing between grid items
              sx={{
                justifyContent: "flex-start",
                alignItems: "flex-start",
              }}
            >
              {proprietaStandardMisurate
                .filter((p) => p.type.influxDbTag !== "novaDiagnostic")
                .concat(proprietaISOMisurate)
                .concat(proprietaRmsMisurate)
                .concat(proprietaFftMisurate)
                .map((proprietaMisurata, index) => (
                  <Grid item xs={4} key={index}>
                    {/* Adjusted to fit max 2 elements per row */}
                    <ProprietaMisurataLabel
                      dispositivo={dispositivo}
                      proprietaMisurata={proprietaMisurata}
                      value={proprietaMisurata.lastValue || null}
                      typographyVariantPrimary="h5"
                      typographyVariantSecondary="caption"
                      alignItems="flex-start"
                    />
                  </Grid>
                ))}
            </Grid>
          </Box>
          <Box
            mt="auto"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              variant="textArrow"
              color="text"
              LinkComponent={Link}
              to={link}
            >
              {t("dispositivoCard.azioni.vediDettaglio")}
            </Button>

            {hasPermission("DispositivoDelete") && (
              <Button
                variant="text"
                size="small"
                color="error"
                startIcon={<Icon>delete_outlined</Icon>}
                sx={{ pb: 0 }}
                onClick={() => {
                  askConfirmation({
                    title: t("dispositivoCard.confirm.titolo"),
                    message: t("dispositivoCard.confirm.messaggio", {
                      matricola: dispositivo.dispositivoMatricola,
                      modello: dispositivo.dispositivoModello?.modello,
                    }),
                    okColor: "error",
                    onConfirm: () => {
                      return deleteDispositivo(dispositivo);
                    },
                  });
                }}
              >
                {t("azioni.elimina")}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

export default DispositivoCard;
